import {
    faEllipsisV,
    faTruck,
    faFlag,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import {
    ButtonGroup,
    CardBody,
    CardTitle,
    CardSubtitle,
    CardFooter,
    UncontrolledTooltip,
    Badge,
} from 'reactstrap';
import cls from 'classnames';
import { FlexStartRow } from '../common/forms/FormElements';
import { Weekdays } from '../Common';
import LongTermStatusIndicator from './LongTermStatusIndicator';
import DailyAttendanceEventIndicator from './DailyAttendanceEventIndicator';
import { isCancelAssignmentTreatedAsUnassigned } from './Dispatch';

export default class StaffCard extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            clientHeight: 0,
        };
    }

    componentDidMount() {
        const div = document.getElementById(`staffCard-${this.props.staff.id}`);

        this.setState({ clientHeight: (div?.getBoundingClientRect().height ?? 0) });
    }

    getFlagEquipment = () => {
        const { staff } = this.props;

        if (!staff.assignedEquipment) {
            return [];
        }

        return staff.assignedEquipment.filter(
            (e) => e.equipmentType.description.toLowerCase().indexOf('flag') > -1,
        );
    };

    getWorkTruckDetails = () => {
        const { staff } = this.props;

        if (!staff.assignedEquipment) {
            return null;
        }

        const workTrucks = staff.assignedEquipment.filter(
            (e) => e.equipmentType.isVehicle,
        );

        if (!workTrucks || workTrucks.length === 0) {
            return null;
        }

        return workTrucks[0];
    };

    isActive = (day) => {
        const { jobs, staff } = this.props;

        return jobs.find(
            (j) => !!j.assignments[day.id].staff.find(
                (g) => !!g.assignment && g.assignment.id == staff.id,
            ),
        );
    };

    isCancelled = (day) => {
        const {
            cancellationJobs,
            staff,
            jobAssignmentCancellationTypeIdsThatPreventReassignment,
            jobAssignmentCancellationTypes,
        } = this.props;

        return cancellationJobs.find((j) => j.assignments[day.id].cancelledAssignments.find(
            (ca) => ca.assignedEmployeeId == staff.id
                && (this.isCancelledButVisible(ca)
                    || ca.thresholdCancel != 0
                    || jobAssignmentCancellationTypeIdsThatPreventReassignment.includes(
                        ca.jobAssignmentCancellationTypeId,
                    ))
                && !isCancelAssignmentTreatedAsUnassigned(
                    ca,
                    jobAssignmentCancellationTypes,
                ),
        ));
    };

    isCancelledButVisible = (cancelledAssignment) => {
        const { jobAssignmentCancellationTypes } = this.props;

        return (
            jobAssignmentCancellationTypes
            && jobAssignmentCancellationTypes.some(
                (jact) => jact.id
                    == cancelledAssignment.jobAssignmentCancellationTypeId
                    && jact.includeOnRosterView,
            )
        );
    };

    hoursWorked = (day) => {
        const { jobs, cancellationJobs, staff } = this.props;
        let total = 0;

        jobs?.forEach(job => {
            const assignment = job.assignments?.[day.id]?.staff?.find(
                s => s.assignment?.id === staff.id
            )?.assignment;

            if (assignment?.hoursWorked) {
                total += assignment?.hoursWorked;
            }
        });

        cancellationJobs?.forEach(cancelledJob => {
            const cancelledAssignment = cancelledJob.assignments?.[day.id]?.cancelledAssignments?.find(
                s => s.assignedEmployeeId === staff.id
            );

            if (cancelledAssignment?.hoursWorked) {
                total += cancelledAssignment.hoursWorked;
            }
        });

        return total;
    };

    isTimesheetSubmitted = (day) => {
        const { jobs, cancellationJobs, staff } = this.props;

        const submissions = jobs
            .map(job => job.assignments[day.id]?.staff?.find(s => s.assignment?.id === staff.id)?.assignment)
            .filter(Boolean);

        const cancelsubmissions = cancellationJobs
            .map(cancelledJob => cancelledJob.assignments[day.id]?.cancelledAssignments?.find(
                s => s.assignedEmployeeId === staff.id)).filter(Boolean);

        return [...submissions, ...cancelsubmissions].some(a => a?.isTimesheetSubmitted);
    };

    getWeeklyHours = () => {
        const { jobs, cancellationJobs, staff } = this.props;
        let total = 0;
        jobs?.forEach(job => {
            const assignments = job.assignments || {};
            Object.values(assignments).forEach(assignmentDay => {
                const staffList = assignmentDay.staff || [];

                staffList.forEach(s => {
                    const assignment = s.assignment;
                    if (assignment?.id === staff.id && assignment.hoursWorked) {
                        total += assignment.hoursWorked;
                    }
                });
            });
        });

        cancellationJobs?.forEach(cancelledJob => {
            const assignments = cancelledJob.assignments || {};

            Object.values(assignments).forEach(assignmentDay => {
                const staffList = assignmentDay.cancelledAssignments || [];

                staffList.forEach(s => {
                    if (s.assignedEmployeeId === staff.id && s.hoursWorked) {
                        total += s.hoursWorked;
                    }
                });
            });
        });

        return total;
    };

    render() {
        const {
            staff,
            provided,
            innerRef,
            group,
            longTermStatuses,
            employeeLongTermStatuses,
            dailyAttendanceEvents,
            employeeDailyAttendanceEvents,
            hiddenStaffIds,
            weekdays,
            compact,
            minimal,
            dragging,
            tenantSettings,
        } = this.props;

        const workTruckDetails = this.getWorkTruckDetails();
        const flagEquipment = this.getFlagEquipment();
        const weeklyHours = this.getWeeklyHours();
        const hoursEnabled = tenantSettings.dispatchHoursEnabled;

        if (hiddenStaffIds.includes(staff.id)) {
            return (
                <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={innerRef}
                />
            );
        }

        const Component = dragging ? 'div' : 'dd';
        const PlaceholderComponent = dragging ? 'dd' : 'div';

        return (
            <>
                <Component
                    id={`staffCard-${staff.id}`}
                    className={cls('card staffCard', {
                        compact: !!compact,
                        minimal: !!minimal,
                        dragging,
                    })}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={innerRef}
                >

                    {!compact && (
                        <div className="dragIndicator">
                            <FontAwesomeIcon icon={faEllipsisV} />
                            <FontAwesomeIcon icon={faEllipsisV} />
                        </div>
                    )}
                    <CardBody>
                        <CardTitle className="card-title">
                            <span className="staff-info">
                                {group && group.assignment && (
                                    <label className="mr-1 dropPlaceholder">{`${group.name}`}</label>
                                )}

                                <span
                                    className="badge badge-lg badge-secondary cursor-pointer mr-1"
                                    id={`groups-${staff.id}`}
                                >
                                    {staff.groups.map(g => g.groupName.charAt(0).toUpperCase()).join(', ')}
                                </span>

                                <UncontrolledTooltip target={`groups-${staff.id}`}>
                                    {staff.groups.map(g => g.groupName).join(', ')}
                                </UncontrolledTooltip>

                                <span
                                    id={`staff-name-${staff.id}`}
                                    className="staff-name"
                                >
                                    {staff.name}
                                </span>
                                <UncontrolledTooltip target={`staff-name-${staff.id}`}>
                                    {staff.name}
                                </UncontrolledTooltip>
                            </span>

                            <div className="status-icons">
                                <DailyAttendanceEventIndicator
                                    staff={staff}
                                    dailyAttendanceEvents={dailyAttendanceEvents}
                                    employeeDailyAttendanceEvents={
                                        employeeDailyAttendanceEvents
                                    }
                                />

                                <LongTermStatusIndicator
                                    staff={staff}
                                    longTermStatuses={longTermStatuses}
                                    employeeLongTermStatuses={
                                        employeeLongTermStatuses
                                    }
                                />
                            </div>
                        </CardTitle>

                        <CardSubtitle className="mb-0 mt-1">
                            <FlexStartRow>
                                <ButtonGroup
                                    className={cls({
                                        'over-40-hours': hoursEnabled && weeklyHours > 40
                                    })}
                                >
                                    {weekdays.map((dayDate) => {
                                        const day = Weekdays.find(
                                            (x) => x.id === dayDate.getDay(),
                                        );
                                        const hours = hoursEnabled ? this.hoursWorked(day) : 0;
                                        const isSubmitted = hoursEnabled ? this.isTimesheetSubmitted(day) : false;
                                        return (
                                            <React.Fragment key={day.id}>
                                                <span
                                                    id={`hours-${staff.id}-${day.id}`}
                                                    className={cls(
                                                        'btn btn-sm btn-outline-secondary scheduledDayIndicator',
                                                        {
                                                            active: this.isActive(day),
                                                            cancelled: this.isCancelled(day),
                                                            'bg-info': isSubmitted,
                                                        },
                                                    )}
                                                >
                                                    {day.abbreviationShort}
                                                </span>
                                                {isSubmitted && (
                                                    <UncontrolledTooltip target={`hours-${staff.id}-${day.id}`} >
                                                        {hours}hr
                                                    </UncontrolledTooltip>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </ButtonGroup>
                            </FlexStartRow>
                        </CardSubtitle>

                        <CardFooter className="mt-2 d-flex align-items-center flex-wrap">
                            {Boolean(workTruckDetails) && (
                                <div
                                    id={`truck-${staff.id}-${workTruckDetails.id}`}
                                    className="cursor-pointer mr-2"
                                >
                                    <FontAwesomeIcon icon={faTruck} />

                                    <UncontrolledTooltip target={`truck-${staff.id}-${workTruckDetails.id}`}>
                                        {workTruckDetails.equipmentType.description} - {workTruckDetails.identifier}
                                    </UncontrolledTooltip>
                                </div>
                            )}

                            {flagEquipment?.length > 0 && flagEquipment.map((flagEquipmentDetails) => (
                                <div
                                    className="cursor-pointer mr-2"
                                    key={flagEquipmentDetails.id}
                                    id={`flag-${staff.id}-${flagEquipmentDetails.id}`}
                                >
                                    <FontAwesomeIcon icon={faFlag} />

                                    <UncontrolledTooltip target={`flag-${staff.id}-${flagEquipmentDetails.id}`}>
                                        {flagEquipmentDetails.equipmentType.description} - ${flagEquipmentDetails.identifier}
                                    </UncontrolledTooltip>
                                </div>
                            ))}

                            {staff.employeeCertifications?.length > 0 && staff.employeeCertifications.map((cert, idx, arr) => (
                                <>
                                    <Badge
                                        key={cert.id}
                                        id={`cert-${cert.id}`}
                                        color="info"
                                        className={cls({
                                            'mr-1': idx < arr.length - 1,
                                        })}
                                    >
                                        {cert.certification.abbreviation}
                                    </Badge>

                                    <UncontrolledTooltip
                                        target={`cert-${cert.id}`}
                                    >
                                        {cert.certification.name}
                                    </UncontrolledTooltip>
                                </>
                            ))}
                        </CardFooter>

                    </CardBody>
                </Component>

                <PlaceholderComponent
                    className="card staffCard placeholder"
                    style={{ height: this.state.clientHeight }}
                />
            </>
        );
    }
}
